import exifr from "exifr";

export interface PhotoInfo {
  /** Small copy for grids, and a medium one for viewing and email. Null when the browser can't read the image (HEIC on Chrome). */
  thumb: Blob | null;
  medium: Blob | null;
  width: number | null;
  height: number | null;
  takenAt: string | null;
  latitude: number | null;
  longitude: number | null;
}

const THUMB_PX = 480;
const MEDIUM_PX = 1600;

async function resized(bitmap: ImageBitmap, maxPx: number, quality: number): Promise<Blob | null> {
  const scale = Math.min(1, maxPx / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", quality));
}

/** EXIF date (with its time zone when the camera recorded one) and GPS position. */
async function readExif(file: File): Promise<Pick<PhotoInfo, "takenAt" | "latitude" | "longitude">> {
  try {
    const tags = await exifr.parse(file, { pick: ["DateTimeOriginal", "OffsetTimeOriginal", "GPSLatitude", "GPSLongitude", "GPSLatitudeRef", "GPSLongitudeRef"], gps: true, reviveValues: false });
    let takenAt: string | null = null;
    const raw = tags?.DateTimeOriginal as string | undefined; // "2026:05:04 15:30:00"
    const m = raw && /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})/.exec(raw);
    if (m) {
      const local = `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}`;
      const offset = typeof tags.OffsetTimeOriginal === "string" && /^[+-]\d{2}:\d{2}$/.test(tags.OffsetTimeOriginal) ? tags.OffsetTimeOriginal : null;
      // Without a recorded offset, assume the photo was taken in this browser's time zone.
      const d = new Date(offset ? `${local}${offset}` : local);
      if (!Number.isNaN(d.getTime())) takenAt = d.toISOString();
    }
    const lat = typeof tags?.latitude === "number" ? tags.latitude : null;
    const lng = typeof tags?.longitude === "number" ? tags.longitude : null;
    return { takenAt, latitude: lat, longitude: lng };
  } catch {
    return { takenAt: null, latitude: null, longitude: null };
  }
}

/** Copies and details for a photo. The original file is uploaded untouched. */
export async function photoInfo(file: File): Promise<PhotoInfo> {
  const exif = await readExif(file);
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const [thumb, medium] = await Promise.all([resized(bitmap, THUMB_PX, 0.8), resized(bitmap, MEDIUM_PX, 0.82)]);
    const info = { thumb, medium, width: bitmap.width, height: bitmap.height, ...exif };
    bitmap.close();
    return info;
  } catch {
    return { thumb: null, medium: null, width: null, height: null, ...exif };
  }
}

export const isImage = (type: string, name: string) => type.startsWith("image/") || /\.(heic|heif)$/i.test(name);

export function formatBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
